import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  createPopper,
  type Instance,
  type Modifier,
  type Placement,
} from '@popperjs/core'
import { v4 as uuid } from 'uuid'

import type { PopperAction } from './ActionsPopper'
import { renderRhapActionsMenuItem } from './rhapActionsPopperUtils'

import styles from './RhapActionsPopper.module.scss'

const DEFAULT_Z_INDEX = 1500

export interface RhapActionsPopperPortaledProps {
  actions?: PopperAction[]
  placement?: Placement
  popperModifiers?: Partial<Modifier<any, any>>[]
  title?: string
  trigger?: React.RefObject<HTMLElement>
  zIndex?: number
}

const RhapActionsPopperPortaled = ({
  actions = [],
  placement = 'bottom',
  popperModifiers = [],
  title,
  trigger,
  zIndex = DEFAULT_Z_INDEX,
}: RhapActionsPopperPortaledProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  )
  const popperInstanceRef = useRef<Instance | null>(null)
  const popperIdRef = useRef(`rhap-actions-popper-${uuid()}`)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popperElement &&
        !popperElement.contains(event.target as Node) &&
        trigger?.current &&
        !trigger.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleTriggerClick = () => {
      setIsOpen((prev) => !prev)
    }

    // Store trigger element in local variable to ensure same reference for cleanup
    const triggerElement = trigger?.current

    if (triggerElement) {
      triggerElement.addEventListener('click', handleTriggerClick)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      if (triggerElement) {
        triggerElement.removeEventListener('click', handleTriggerClick)
      }
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [trigger, popperElement, isOpen])

  useEffect(() => {
    if (!trigger?.current || !popperElement || !isOpen) {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
        popperInstanceRef.current = null
      }
      return undefined
    }

    popperInstanceRef.current = createPopper(trigger.current, popperElement, {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'left', 'right'],
          },
        },
        ...popperModifiers,
      ],
    })

    return () => {
      if (popperInstanceRef.current) {
        popperInstanceRef.current.destroy()
        popperInstanceRef.current = null
      }
    }
  }, [trigger, popperElement, isOpen, placement, popperModifiers])

  if (!isOpen || !actions.length) {
    return null
  }

  return createPortal(
    <div
      className={styles.tooltip}
      id={popperIdRef.current}
      ref={setPopperElement}
      role="menu"
      style={{ zIndex }}
    >
      <div className={styles.actionsContainer}>
        {title && <div className={styles.title}>{title}</div>}
        {actions.filter(Boolean).map((item: PopperAction) => (
          <div key={item.id}>
            {renderRhapActionsMenuItem({
              ...item,
              onClose: () => setIsOpen(false),
            })}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  )
}

export default RhapActionsPopperPortaled
